import React, { useState } from "react";
import { motion } from "framer-motion";
import api from "../../store/services/api";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PersonIcon from "@mui/icons-material/Person";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Animation variants
const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  hover: {
    y: -3,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
  tap: {
    scale: 0.98,
  },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

export default function NamedRow({
  id,
  index = null,
  name,
  type = null,
  editAction,
  deleteAction,
  disableActions = false,
}) {
  const theme = useTheme();
  const [userType, setUserType] = useState(type);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const promoteUser = async () => {
    try {
      setLoading(true);
      await api.put(`/user/operators/promote/?id=${id}`);
      setUserType(1);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const demoteUser = async () => {
    try {
      setLoading(true);
      await api.put(`/user/operators/demote/?id=${id}`);
      setUserType(2);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const alertEditMotherUser = () => {
    alert(
      "Não é possível editar o usuário mãe por aqui, faça login e entre em Detalhes da Conta"
    );
  };

  // Get role information based on user type
  const getRoleInfo = () => {
    switch (userType) {
      case 0:
        return {
          label: "Proprietário",
          icon: <LocalPoliceIcon fontSize="small" />,
          color: theme.palette.error.main,
          action: alertEditMotherUser,
          tooltip: "Proprietário - Usuário principal",
        };
      case 1:
        return {
          label: "Gerente",
          icon: <SupervisorAccountIcon fontSize="small" />,
          color: theme.palette.warning.main,
          action: demoteUser,
          tooltip: "Rebaixar para Operador",
        };
      case 2:
        return {
          label: "Operador",
          icon: <PersonIcon fontSize="small" />,
          color: theme.palette.info.main,
          action: promoteUser,
          tooltip: "Promover para Gerente",
        };
      default:
        return {
          label: "Indefinido",
          icon: <HelpOutlineIcon fontSize="small" />,
          color: theme.palette.grey[500],
          action: null,
          tooltip: "Tipo de usuário indefinido",
        };
    }
  };

  const roleInfo = getRoleInfo();

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      whileHover={disableActions ? {} : "hover"}
      whileTap={disableActions ? {} : "tap"}
      layout
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      <Paper
        elevation={hovered ? 2 : 0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 2,
          backgroundColor: hovered
            ? alpha(theme.palette.background.paper, 0.9)
            : theme.palette.grey[50],
          border: `1px solid ${
            hovered
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.grey[300], 0.5)
          }`,
          transition: "all 0.3s ease",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1 }}
          >
            {/* User avatar/index */}
            {index && (
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  width: 36,
                  height: 36,
                  fontWeight: "bold",
                }}
              >
                {index}
              </Avatar>
            )}

            {/* User name */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {name}
              </Typography>

              {/* Role chip for mobile */}
              <Box sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}>
                <Chip
                  icon={roleInfo.icon}
                  label={roleInfo.label}
                  size="small"
                  sx={{
                    backgroundColor: alpha(roleInfo.color, 0.1),
                    color: roleInfo.color,
                    "& .MuiChip-icon": {
                      color: roleInfo.color,
                    },
                    height: 24,
                  }}
                />
              </Box>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-end" },
            }}
          >
            {/* Role management */}
            {userType !== undefined && userType !== null && (
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Tooltip title={roleInfo.tooltip}>
                    <Chip
                      icon={roleInfo.icon}
                      label={roleInfo.label}
                      size="small"
                      onClick={roleInfo.action}
                      sx={{
                        backgroundColor: alpha(roleInfo.color, 0.1),
                        color: roleInfo.color,
                        "& .MuiChip-icon": {
                          color: roleInfo.color,
                        },
                        cursor: userType === 0 ? "default" : "pointer",
                        "&:hover": {
                          backgroundColor:
                            userType === 0
                              ? alpha(roleInfo.color, 0.1)
                              : alpha(roleInfo.color, 0.2),
                        },
                        height: 28,
                        fontWeight: 500,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" spacing={1}>
              {!disableActions ? (
                <>
                  <Tooltip title="Editar usuário">
                    <motion.div
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <IconButton
                        size="small"
                        onClick={editAction}
                        sx={{
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.2
                            ),
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </motion.div>
                  </Tooltip>
                  <Tooltip title="Excluir usuário">
                    <motion.div
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <IconButton
                        size="small"
                        onClick={deleteAction}
                        sx={{
                          color: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.2
                            ),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </motion.div>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Não é possível editar o proprietário">
                    <span>
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          color: theme.palette.action.disabled,
                          backgroundColor: alpha(
                            theme.palette.action.disabled,
                            0.1
                          ),
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Não é possível remover o proprietário">
                    <span>
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          color: theme.palette.action.disabled,
                          backgroundColor: alpha(
                            theme.palette.action.disabled,
                            0.1
                          ),
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
}
